import { useCallback } from 'react';

import { createQuestionGroup, deleteQuestionGroup, updateQuestionGroup } from '@api/instant_survey';

import { createQuestionKey } from './helpers';

/**
 * useQuestionGroupHelpers
 * Question group create/rename/randomize/assign callbacks. Read survey state via surveyRef so
 * callbacks don't need survey in deps.
 * @param {Object} params
 * @param {Object} params.surveyRef ref whose `.current` holds the live survey
 * @param {Function} params.updateSurvey survey state updater
 * @return {{
 *   assignQuestionToGroup: Function,
 *   createGroupForQuestion: Function,
 *   renameGroup: Function,
 *   toggleGroupRandomize: Function,
 * }}
 */
export const useQuestionGroupHelpers = ({ surveyRef, updateSurvey }) => {
  const createGroupForQuestion = useCallback(
    async (question) => {
      const targetKey = createQuestionKey(question);
      const group = await createQuestionGroup({ name: 'Question Group' });
      updateSurvey(
        {
          questionGroups: {
            ...(surveyRef.current.questionGroups || {}),
            [String(group.id)]: group,
          },
          questions: surveyRef.current.questions.map((q) =>
            createQuestionKey(q) === targetKey ? { ...q, questionGroup: group.id } : q
          ),
        },
        { markDirty: true }
      );
    },
    [surveyRef, updateSurvey]
  );

  const renameGroup = useCallback(
    async (groupId, name) => {
      await updateQuestionGroup({ id: groupId, name });
      updateSurvey(
        {
          questionGroups: {
            ...(surveyRef.current.questionGroups || {}),
            [String(groupId)]: {
              ...(surveyRef.current.questionGroups?.[String(groupId)] || { id: groupId }),
              name,
            },
          },
        },
        { markDirty: false }
      );
    },
    [surveyRef, updateSurvey]
  );

  const toggleGroupRandomize = useCallback(
    async (groupId) => {
      const existing = surveyRef.current.questionGroups?.[String(groupId)];
      const nextRandomize = !(existing?.randomizeOrder ?? false);
      await updateQuestionGroup({ id: groupId, randomizeOrder: nextRandomize });
      updateSurvey(
        {
          questionGroups: {
            ...(surveyRef.current.questionGroups || {}),
            [String(groupId)]: {
              ...(existing || { id: groupId }),
              randomizeOrder: nextRandomize,
            },
          },
        },
        { markDirty: false }
      );
    },
    [surveyRef, updateSurvey]
  );

  // Move a question into a group (groupId) or out of its group (null), repositioning it so groups
  // stay contiguous (buildRenderList only collapses consecutive same-group questions):
  //  - into a group  → inserted as the group's LAST member;
  //  - out of a group → moved to directly below its old group's run (the question is kept).
  // Groups left without members are auto-deleted.
  const assignQuestionToGroup = useCallback(
    (question, groupId) => {
      const targetKey = createQuestionKey(question);
      const questions = surveyRef.current.questions;
      const moving = questions.find((q) => createQuestionKey(q) === targetKey);
      if (!moving) return;
      const oldGroupId = moving.questionGroup ?? null;
      const originalIndex = questions.findIndex((q) => createQuestionKey(q) === targetKey);

      const without = questions.filter((q) => createQuestionKey(q) !== targetKey);

      // Index of the last member of a given group within `without` (-1 if none remain).
      const lastMemberIndex = (gId) =>
        gId == null
          ? -1
          : without.reduce((acc, q, idx) => (q.questionGroup === gId ? idx : acc), -1);

      let insertAt;
      if (groupId != null) {
        const lastIdx = lastMemberIndex(groupId);
        insertAt = lastIdx === -1 ? without.length : lastIdx + 1;
      } else {
        // Ungrouping: drop directly below the old group's run; if that group is now empty (or the
        // question had no group), keep it where it was.
        const lastIdx = lastMemberIndex(oldGroupId);
        insertAt = lastIdx === -1 ? Math.min(originalIndex, without.length) : lastIdx + 1;
      }

      const updatedQuestions = [...without];
      updatedQuestions.splice(insertAt, 0, { ...moving, questionGroup: groupId });

      // auto-remove groups that have become empty after this assignment
      const remainingGroupIds = new Set(
        updatedQuestions.filter((q) => q.questionGroup != null).map((q) => String(q.questionGroup))
      );

      const currentGroups = surveyRef.current.questionGroups || {};
      const orphanIds = Object.keys(currentGroups).filter((id) => !remainingGroupIds.has(id));

      orphanIds.forEach((id) => {
        deleteQuestionGroup({ id: Number(id) }).catch(() => {
          // Best-effort — a failed DELETE leaves an orphaned DB row but doesn't break the UI.
        });
      });

      const nextGroups = { ...currentGroups };
      orphanIds.forEach((id) => delete nextGroups[id]);

      updateSurvey(
        { questionGroups: nextGroups, questions: updatedQuestions },
        { markDirty: true }
      );
    },
    [surveyRef, updateSurvey]
  );

  return {
    assignQuestionToGroup,
    createGroupForQuestion,
    renameGroup,
    toggleGroupRandomize,
  };
};
